// Review state for screen 03 (Review & refine) of the "update from data" flow.
// Shape mirrors the review-state contract described by US-12-01 / US-12-02.

/** Decision recorded against a single change candidate (US-13-01). */
export type ChangeStatus = 'Pending' | 'Accepted' | 'Declined' | 'Edited';

/** How the detector matched the value: an exact data hit, a heuristic guess, or a human edit. */
export type MatchKind = 'Exact' | 'Heuristic' | 'Manual';

/** Outcome of re-verifying a value against the ingested data (US-5-02 / US-5-03 / US-5-04). */
export type VerificationStatus = 'Verified' | 'Unverified' | 'Conflicting' | 'Untraceable';

/** Free text: the API's candidateType, humanised for display (e.g. "Proposed edit"). */
export type ChangeType = string;

/** Source cell trail attached to every data-derived value (US-5-01). */
export interface ChangeProvenance {
  provenanceId?: number;
  changeId?: number;
  sectionId?: number;
  fileId?: number;
  fileName: string;
  sheet?: string | null;
  sheetName?: string | null;
  cellRef?: string | null;
  cell?: string | null;
  columnName?: string | null;
  rawValue?: string | null;
  value?: string | null;
  /** Null when the API does not verify values — the verification pill is then hidden. */
  verificationStatus?: VerificationStatus | null;
}

/** A single proposed replacement inside a section (US-7-01, US-9-01). */
export interface ChangeCandidate {
  id: number;
  sectionId: number;
  changeType?: ChangeType;
  /** The API's raw candidateType, e.g. 'PROPOSED_EDIT' or 'VERIFY_FLAG' — changeType is its display form. */
  candidateType: string;
  matchKind?: MatchKind;
  /** Null when the detector does not report one. */
  confidence?: number | null;
  isDateFlagged?: boolean;
  /** Why the detector raised this candidate, e.g. 'UNCHANGED_REVIEW_REQUIRED'. */
  triggerReason?: string | null;
  source?: string;
  status: ChangeStatus;
  originalHtml?: string;
  proposedHtml?: string;
  editedHtml?: string | null;
  originalText?: string;
  proposedText?: string;
  editedText?: string | null;
  dataLabel?: string;
  rationale?: string;
  provenance?: ChangeProvenance[];
  provenances?: ChangeProvenance[];
  /**
   * True only for a manual edit created in the browser. Such a change has no server-side id,
   * so its decisions are held locally instead of posted. Never inferred from the API's
   * `source` — a server change may legitimately be user-originated.
   */
  isLocal?: boolean;
}

/** A report section carrying its change candidates and a changed/unchanged marker (US-9-02). */
export interface ReviewSection {
  id: number;
  sectionNumber: string;
  sectionTitle: string;
  sectionSequence: number;
  parentSectionId: number | null;
  changed: boolean;
  changeCount: number;
  pendingCount: number;
  isEditable: boolean;
  contentHtml: string;
  previousContentHtml: string;
  changes: ChangeCandidate[];
  version?: string;
}

/** Response from GET /api/v2/Reports/{reportId}/sections/{sectionId}/edit-text */
export interface EditSectionTextResponse {
  text: string;
  version: string;
  heading: string;
  lineCount: number;
  editableLineCount: number;
}

export interface ReviewDetector {
  name: string;
  version: string;
}

export interface ReviewMetrics {
  changed: number;
  unchanged: number;
  dateFlags: number;
}

/** Decision progress behind the review summary bar (US-13-03). */
export interface ReviewProgress {
  total: number;
  accepted: number;
  declined: number;
  pending: number;
}

export interface ReviewState {
  reportId: number;
  reportName: string;
  clientName: string;
  siteName: string;
  status: number;
  statusLabel: string;
  detector: ReviewDetector;
  dataSourceCount: number;
  sectionCount: number;
  metrics: ReviewMetrics;
  progress: ReviewProgress;
  sections: ReviewSection[];
}

export type DecisionAction = Exclude<ChangeStatus, 'Pending'>;

/** One append-only decision log entry (US-13-02). */
export interface ReviewDecision {
  reportId: number;
  sectionId: number;
  changeId: number;
  action: DecisionAction;
  editedHtml: string | null;
  decidedBy: string;
  decidedAtUtc: string;
}

/** Contents-rail node — sections nested to arbitrary depth by parentSectionId. */
export interface ContentsNode {
  section: ReviewSection;
  children: ContentsNode[];
}

/**
 * A verify flag asks someone to read the section; it proposes no edit. It is therefore not a
 * change and never pending — it is reported by its own "Review Required" badge instead.
 */
export function isVerifyFlag(change: ChangeCandidate): boolean {
  return (change.candidateType ?? '').toUpperCase() === 'VERIFY_FLAG';
}
